import { useLocale, useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { ApplicableCouponTag } from '@/src/types/applicableCoupons'

import { FlightDetailsOffers, FlightDetailsOfferItem } from './flightDetailsOffers'

interface FlightDetailsOffersWrapperProps {
    tags: ApplicableCouponTag[]
    offersContainerRef?: React.RefObject<HTMLDivElement | null>
    onOffersCountChange?: (count: number) => void
}

const FlightDetailsOffersWrapper: React.FC<FlightDetailsOffersWrapperProps> = ({
    tags,
    offersContainerRef,
    onOffersCountChange
}) => {
    const tFlightDetails = useTranslations('srpFlightDetails')
    const tCoupon = useTranslations('couponTray')
    const tCommon = useTranslations('common')
    const locale = useLocale()
    const { formatCurrency } = useFormatCurrencyForSrp()
    const [copiedOfferId, setCopiedOfferId] = useState<string | null>(null)

    const offers = useMemo<FlightDetailsOfferItem[]>(() => {
        const mappedOffers = (tags || [])
            .filter(tag => tag.status)
            .map(tag => {
                const localized = locale === 'ar' ? tag.ar : tag.en
                const fallback = tag.en
                const amount = Number(tag.amount)
                const amountHeading =
                    Number.isFinite(amount) && amount > 0
                        ? tCoupon('flatAmountOff', { amount: formatCurrency(amount) })
                        : ''

                return {
                    id: tag._id,
                    code: tag.code,
                    amount,
                    prioritySort: !!tag.prioritySort,
                    heading: amountHeading,
                    description: localized.description || fallback.description,
                    targetLink: tag.targetLink
                }
            })
            .filter(tag => !!tag.heading)

        const priorityOffers = mappedOffers
            .filter(offer => offer.prioritySort)
            .sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0))

        const nonPriorityOffers = mappedOffers
            .filter(offer => !offer.prioritySort)
            .sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0))

        return [...priorityOffers, ...nonPriorityOffers]
    }, [formatCurrency, locale, tCoupon, tags])

    useEffect(() => {
        onOffersCountChange?.(offers.length)
    }, [offers.length, onOffersCountChange])

    const handleCopyCode = useCallback((offerId: string, code: string) => {
        if (typeof navigator !== 'undefined' && navigator?.clipboard) {
            void navigator.clipboard.writeText(code)
        }
        setCopiedOfferId(offerId)
        setTimeout(() => {
            setCopiedOfferId(prev => (prev === offerId ? null : prev))
        }, 1200)
    }, [])

    const handleKnowMore = useCallback((offerId: string) => {
        const offer = tags.find(tag => tag._id === offerId)
        if (!offer?.targetLink || typeof window === 'undefined') return
        window.open(offer.targetLink, '_blank', 'noopener,noreferrer')
    }, [tags])

    return (
        <FlightDetailsOffers
            sectionTitle={tFlightDetails('specialOffersForYou')}
            sectionSubtitle={tFlightDetails('offersAppliedAtCheckout')}
            knowMoreLabel={tCommon('knowMore')}
            offers={offers}
            containerRef={offersContainerRef}
            copiedOfferId={copiedOfferId}
            onCopyCode={handleCopyCode}
            onKnowMore={handleKnowMore}
        />
    )
}

export { FlightDetailsOffersWrapper }
export default FlightDetailsOffersWrapper

